import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { mockGalleryImages } from '../../data/mockData';

const Hero = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoSliding, setIsAutoSliding] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  const images = mockGalleryImages.slice(0, 5).sort((a, b) => a.order - b.order);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);
    mediaQuery.addEventListener('change', updatePreference);
    return () => mediaQuery.removeEventListener('change', updatePreference);
  }, []);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | null = null;

    if (isAutoSliding && !prefersReducedMotion) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % images.length);
      }, 5000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAutoSliding, images.length, prefersReducedMotion]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % images.length);
    setIsAutoSliding(false);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + images.length) % images.length);
    setIsAutoSliding(false);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
    setIsAutoSliding(false);
  };

  return (
    <section className="relative flex min-h-[38rem] items-center lg:min-h-[42rem]">
      {/* Background Slideshow */}
      <div className="absolute inset-0 overflow-hidden">
        {images.map((image, index) => (
          <div
            key={image.id}
            className={`absolute inset-0 transition-[opacity,transform] duration-1000 ease-in-out motion-reduce:transition-none motion-reduce:scale-100 ${
              index === currentSlide
                ? 'opacity-100 scale-100'
                : 'opacity-0 scale-105'
            }`}
            style={{
              transform: index === currentSlide || prefersReducedMotion ? 'translateX(0)' : 'translateX(20px)',
            }}
          >
            <img
              src={image.heroUrl ?? image.url}
              alt=""
              className="w-full h-full object-cover"
              width={1920}
              height={1280}
              loading={index === 0 ? 'eager' : 'lazy'}
              decoding="async"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-black/5" />
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 py-16 pb-32 sm:px-8 lg:px-12">
        <div className="max-w-3xl space-y-5 text-white">
            <div className="flex items-center space-x-2">
              <Sparkles className="h-5 w-5 text-rizes-olive" />
              <span className="rounded-full border border-white/35 bg-black/15 px-3 py-1 text-xs font-semibold uppercase text-white/95">
                SIFNOS · CYCLADES
              </span>
            </div>

            <h1 className="max-w-3xl font-serif text-4xl leading-[1.08] sm:text-5xl lg:text-6xl">
              Rizes, a quiet house in Sifnos
            </h1>

            <p className="max-w-xl text-lg leading-7 text-white/90 sm:text-xl">
              Two bedrooms. A quiet spot, yet close to Apollonia.
            </p>

            <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:gap-4">
              <a
                href="#book"
                className="inline-flex items-center justify-center rounded-md border border-white bg-white px-5 py-3 font-medium text-aegean-blue transition-colors hover:bg-rizes-ivory focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <span>Check Availability</span>
              </a>
              <a
                href="#gallery"
                className="inline-flex items-center justify-center rounded-md border border-white/75 bg-black/10 px-5 py-3 font-medium text-white transition-colors hover:bg-white/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
              >
                <span>Explore the House</span>
              </a>
            </div>

          </div>
        </div>

      {/* Slide Controls */}
      <div className="absolute bottom-5 left-1/2 z-20 flex -translate-x-1/2 items-center space-x-3 rounded-full border border-white/20 bg-black/20 px-3 py-2 backdrop-blur-sm sm:bottom-6 sm:space-x-4 sm:px-4">
        <button
          onClick={prevSlide}
          aria-label="Previous hero image"
          className="flex h-10 w-10 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:transition-none"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        
        <div className="flex items-center space-x-2">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              aria-label={`Show hero image ${index + 1}`}
              aria-pressed={index === currentSlide}
              className={`h-2.5 w-2.5 rounded-full transition-all motion-reduce:transition-none ${
                index === currentSlide
                  ? 'bg-primary-400 scale-110'
                  : 'bg-white/50 hover:bg-white/75'
              }`}
            />
          ))}
        </div>
        
        <button
          onClick={nextSlide}
          aria-label="Next hero image"
          className="flex h-10 w-10 items-center justify-center rounded-full text-white transition-colors hover:bg-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white motion-reduce:transition-none"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-6 right-5 z-20 hidden sm:block">
        <div className="flex h-10 w-10 items-center justify-center rounded-full border border-white/35 bg-black/15 backdrop-blur-sm">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </section>
  );
};

export default Hero;
